import type { TranslocoService } from '@jsverse/transloco';
import { DEFAULT_LANG, type SupportedLang } from '../constants/languages';

/** Transloco language -> BCP 47 locale used by Intl formatters (one entry per SUPPORTED_LANGS). */
const LOCALE_BY_LANG: Record<SupportedLang, string> = {
	es: 'es-ES',
	en: 'en-GB',
	zh: 'zh-CN',
};

const DEFAULT_LOCALE = LOCALE_BY_LANG[DEFAULT_LANG];

/** Locale for a Transloco language (es-ES when unknown). */
export function localeForLang(lang: string): string {
	return LOCALE_BY_LANG[lang as SupportedLang] ?? DEFAULT_LOCALE;
}

/** Locale matching the active Transloco language (es-ES when unknown). */
export function activeLocale(transloco: TranslocoService): string {
	return localeForLang(transloco.getActiveLang());
}

function capitalize(text: string, locale: string): string {
	return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

/** The 12 month names for the locale, capitalized ('Ene'... / 'Enero'... in es-ES). */
export function monthNames(locale: string, width: 'short' | 'long'): string[] {
	const format = new Intl.DateTimeFormat(locale, { month: width });
	return Array.from({ length: 12 }, (_, month) => capitalize(format.format(new Date(2000, month, 1)), locale));
}

/** Month and year ('Septiembre de 2026' in es-ES, 'September 2026' in en-GB). */
export function formatMonthYear(year: number, month: number, locale: string): string {
	const text = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(year, month, 1));
	return capitalize(text, locale);
}

function toNumber(value: unknown): number | null {
	if (value === null || value === undefined || value === '') return null;
	const n = Number(value);
	return Number.isNaN(n) ? null : n;
}

function toDate(value: string | number | Date | null | undefined): Date | null {
	if (value === null || value === undefined || value === '') return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

/** Amount in euros formatted for the locale ('12,50 €' in es-ES); '-' when there is no number. */
export function formatEur(value: unknown, locale: string): string {
	const n = toNumber(value);
	return n === null ? '-' : new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(n);
}

/** Short date ('24/9/2026' in es-ES); '' when there is no valid date. */
export function formatDate(value: string | number | Date | null | undefined, locale: string): string {
	const date = toDate(value);
	return date ? date.toLocaleDateString(locale) : '';
}

/** Hours and minutes ('14:05'); '' when there is no valid date. */
export function formatTime(value: string | number | Date | null | undefined, locale: string): string {
	const date = toDate(value);
	return date ? date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '';
}

/** Short date and time; '-' when there is no valid date. */
export function formatDateTime(value: string | number | Date | null | undefined, locale: string): string {
	const date = toDate(value);
	return date ? date.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' }) : '-';
}

/**
 * Angular-style digits info ('minInt.minFrac-maxFrac', e.g. '1.2-2') as Intl options.
 * Missing parts keep Angular's defaults (1 integer digit, 0-3 fraction digits).
 */
export function digitsOptions(digitsInfo?: string): Intl.NumberFormatOptions {
	const match = digitsInfo?.match(/^(\d+)?(?:\.(\d+)?(?:-(\d+))?)?$/);
	if (!digitsInfo || !match) return {};
	const options: Intl.NumberFormatOptions = {};
	if (match[1] !== undefined) options.minimumIntegerDigits = Number(match[1]);
	if (match[2] !== undefined) options.minimumFractionDigits = Number(match[2]);
	if (match[3] !== undefined) options.maximumFractionDigits = Number(match[3]);
	return options;
}

const numberFormats = new Map<string, Intl.NumberFormat>();

function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
	const key = `${locale}|${JSON.stringify(options)}`;
	let format = numberFormats.get(key);
	if (!format) {
		format = new Intl.NumberFormat(locale, options);
		numberFormats.set(key, format);
	}
	return format;
}

/** Number for the locale ('1.234,5' in es-ES); '' when there is no number. */
export function formatNumber(value: unknown, locale: string, digitsInfo?: string): string {
	const n = toNumber(value);
	return n === null ? '' : numberFormat(locale, digitsOptions(digitsInfo)).format(n);
}

/** Currency amount for the locale ('12,50 €' in es-ES, '€12.50' in en-GB); '' when there is no number. */
export function formatCurrency(value: unknown, locale: string, currency = 'EUR', digitsInfo = '1.2-2'): string {
	const n = toNumber(value);
	return n === null
		? ''
		: numberFormat(locale, { style: 'currency', currency, ...digitsOptions(digitsInfo) }).format(n);
}

/** Two-digit day/month, full year, hours and minutes ('24/09/2026, 14:05' in es-ES); '' when no valid date. */
export function formatDateTimeNumeric(value: string | number | Date | null | undefined, locale: string): string {
	const date = toDate(value);
	return date
		? date.toLocaleString(locale, {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			})
		: '';
}

/** File size in B/KB/MB with one decimal for the locale ('1,5 MB' in es-ES). */
export function formatFileSize(bytes: number, locale: string): string {
	const format = numberFormat(locale, { maximumFractionDigits: 1 });
	if (bytes < 1024) return `${format.format(bytes)} B`;
	if (bytes < 1024 * 1024) return `${format.format(bytes / 1024)} KB`;
	return `${format.format(bytes / (1024 * 1024))} MB`;
}
